import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { listLeads, type LeadStatus } from "../api/leads.ts";

const STATUSES: LeadStatus[] = ["new", "assigned_to_tech", "canceled"];

export default function LeadsPage() {
  const { t } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const statusParam = sp.get("status") as LeadStatus | null;
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["leads", statusParam, q],
    queryFn: () => listLeads({ status: statusParam ?? undefined, q: q || undefined, limit: 100 }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t("leads.title")}</h1>
        <input className="input w-full sm:max-w-xs" placeholder={t("common.search") ?? ""} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={chip(!statusParam)} onClick={() => setSp({})}>All</button>
        {STATUSES.map((s) => (
          <button key={s} type="button" className={chip(statusParam === s)} onClick={() => setSp({ status: s })}>
            {t(`leads.status.${s}`)}
          </button>
        ))}
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="hidden px-4 py-2 md:table-cell">When</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Phone</th>
              <th className="hidden px-4 py-2 md:table-cell">Service</th>
              <th className="hidden px-4 py-2 md:table-cell">Triage</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{t("leads.empty")}</td></tr>
            )}
            {data?.items.map((lead) => (
              <tr key={lead.id} className="hover:bg-slate-50">
                <td className="hidden px-4 py-2 text-slate-500 md:table-cell">{new Date(lead.created_at).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <Link to={`/leads/${lead.id}`} className="font-medium text-brand-700 hover:underline">
                    {lead.name ?? "(no name)"}
                  </Link>
                  <div className="mt-0.5 text-[11px] text-slate-500 md:hidden">
                    {[
                      lead.service_type,
                      new Date(lead.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                <td className="px-4 py-2">{lead.phone_e164 ?? "—"}</td>
                <td className="hidden px-4 py-2 md:table-cell">{lead.service_type ?? "—"}</td>
                <td className="hidden px-4 py-2 md:table-cell">
                  {lead.urgency ? <TriageBadge urgency={lead.urgency} score={lead.triage_score} /> : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{t(`leads.status.${lead.status}`)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TriageBadge({ urgency, score }: { urgency: string; score: number | null }) {
  const map: Record<string, string> = {
    emergency: "bg-red-100 text-red-800",
    same_day: "bg-orange-100 text-orange-800",
    scheduled: "bg-blue-100 text-blue-700",
    info: "bg-slate-100 text-slate-600",
  };
  const label = urgency.replace("_", " ");
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${map[urgency] ?? "bg-slate-100 text-slate-700"}`}>
      {label}{score != null ? ` · ${score}` : ""}
    </span>
  );
}

function chip(active: boolean) {
  return active
    ? "rounded-full bg-brand-600 px-3 py-1 text-sm text-white"
    : "rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50";
}
